import {
    IonBadge,
    IonButton,
    IonItem,
    IonItemDivider,
    IonItemGroup,
    IonLabel,
    IonList,
    IonListHeader,
    IonRefresher,
    IonRefresherContent,
    RefresherEventDetail,
    useIonLoading,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { compare } from 'compare-versions';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import LoadableContent from '../../../components/content/LoadableContent';
import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import { IAssetStateData } from '../../../interfaces/IAssetState';
import { IInstallationRouteProps } from '../../../interfaces/IRoute';
import {
    roomsWithDevicesAndStateAtom,
    skipFwUpdateAtom,
    tabsEnabledAtom,
    updateDBStateAtom,
} from '../../../store/Atoms';
import {
    getDevicesFirmwareStatusRequest,
    getMeshStatusRequest,
    pollFirmwareStatusForDevices,
    updateDevicesFirmwareRequest,
} from '../../../store/Requests';
import { TFirmwareUptodateness } from '../../../types/Generic';
import { THomeAssetData } from '../../../types/HomeAssetData';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';

const POLLING_INTERVAL_IN_SECONDS = 15;
const MAX_AMOUNT_OF_POLLS = 600 / POLLING_INTERVAL_IN_SECONDS; // 600 seconds = 10 minutes
const MIN_FW_VERSION_FOR_PARALLEL_UPDATES = '1.1.6';

const MeshStatus: React.FC<IInstallationRouteProps> = ({ match }) => {
    const location = useLocation();
    const router = useCustomRouter();
    const jobId = +match.params.jobId;
    const [presentToast] = useIonToast();
    const [presentFwLoading, dismissFwLoading] = useIonLoading();
    const meshStatusTimeout = useRef<
        ReturnType<typeof setTimeout> | undefined
    >(undefined);
    const [, setTabsEnabled] = useAtom(tabsEnabledAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [value] = useAtom(roomsWithDevicesAndStateAtom);
    const [skipFwUpdate] = useAtom(skipFwUpdateAtom);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [devicesBeingUpdated, setDevicesBeingUpdated] = useState<
        THomeAssetData[] | undefined
    >(undefined);
    const [meshStatus, setMeshStatus] = useState<{
        [key: string]: IAssetStateData;
    }>();
    const [firmwareStatus, setFirmwareStatus] = useState<{
        [key: string]: TFirmwareUptodateness;
    }>();
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data : []),
        [value]
    );
    const allDevices = useMemo(
        () =>
            rooms.reduce(
                (acc, room) => [...acc, ...room.devices],
                [] as THomeAssetData[]
            ),
        [rooms]
    );
    const numberOfDevices = allDevices.length;
    const devicesWithOldFw = useMemo(() => {
        if (
            meshStatus &&
            firmwareStatus &&
            Object.values(firmwareStatus).length === numberOfDevices
        ) {
            const filteredDevices = allDevices.filter((device) => {
                const status = meshStatus && meshStatus[device.homeAssetId];
                const fwStatus =
                    firmwareStatus[device.macAddress.padStart(16, 'F')];

                return (
                    status &&
                    status.online &&
                    [
                        TFirmwareUptodateness.UNKNOWN_VERSION,
                        TFirmwareUptodateness.TOOOLD,
                        TFirmwareUptodateness.UPDATING,
                    ].includes(fwStatus!)
                );
            });

            const updatesInParallel: THomeAssetData[] = [];
            const updatesInSequence: THomeAssetData[][] = [];

            filteredDevices.forEach((device) => {
                const status = meshStatus && meshStatus[device.homeAssetId];

                if (
                    status &&
                    compare(
                        status.firmwareVersion,
                        MIN_FW_VERSION_FOR_PARALLEL_UPDATES,
                        '>='
                    )
                ) {
                    updatesInParallel.push(device);
                } else {
                    updatesInSequence.push([device]);
                }
            });

            return updatesInParallel.length
                ? [...updatesInSequence, updatesInParallel]
                : updatesInSequence;
        }

        return [];
    }, [allDevices, firmwareStatus, numberOfDevices, meshStatus]);
    const allOnline = useMemo(() => {
        return meshStatus &&
            Object.values(meshStatus).length === numberOfDevices
            ? Object.values(meshStatus).every((status) => status.online)
            : false;
    }, [meshStatus, numberOfDevices]);
    const allFwUpToDate = skipFwUpdate || devicesWithOldFw.length === 0;

    const loadMeshStatus = useCallback(async () => {
        try {
            const [meshStatus, firmwareStatus] = await Promise.all([
                getMeshStatusRequest(),
                getDevicesFirmwareStatusRequest(),
            ]);
            setMeshStatus(meshStatus);
            setFirmwareStatus(firmwareStatus);

            if (firmwareStatus && meshStatus) {
                const devicesBeingUpdated = allDevices.filter((device) => {
                    const fwStatus =
                        firmwareStatus[device.macAddress.padStart(16, 'F')];
                    const status = meshStatus[device.homeAssetId];
                    return (
                        fwStatus === TFirmwareUptodateness.UPDATING &&
                        status &&
                        status.online
                    );
                });
                if (devicesBeingUpdated && devicesBeingUpdated.length > 0) {
                    setDevicesBeingUpdated([...devicesBeingUpdated]);
                }
            }
        } catch (e) {
            presentToast({
                message: t`Oops, something went wrong while loading mesh status!`,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
    }, [allDevices, presentToast]);

    const stopMeshStatusPolling = useCallback(() => {
        meshStatusTimeout.current && clearTimeout(meshStatusTimeout.current);
        meshStatusTimeout.current = undefined;
    }, []);

    const startMeshStatusPolling = useCallback(async () => {
        let count = 0;
        const startTimeout = () => {
            stopMeshStatusPolling();
            meshStatusTimeout.current = setTimeout(async () => {
                count += 1;
                setRefreshLoading(true);
                await loadMeshStatus();
                setRefreshLoading(false);
                if (count < MAX_AMOUNT_OF_POLLS) {
                    startTimeout();
                }
            }, POLLING_INTERVAL_IN_SECONDS * 1000);
        };
        startTimeout();
    }, [loadMeshStatus, stopMeshStatusPolling]);

    const handleRefresh = useCallback(
        async (event: CustomEvent<RefresherEventDetail>) => {
            setRefreshLoading(true);
            await loadMeshStatus();
            event.detail.complete();
            setRefreshLoading(false);
        },
        [loadMeshStatus]
    );

    const onRefreshClick = useCallback(async () => {
        setRefreshLoading(true);
        await loadMeshStatus();
        setRefreshLoading(false);
    }, [loadMeshStatus]);

    const updateDeviceFw = useCallback(
        async (devices: THomeAssetData[], updating: boolean = false) => {
            const macs = devices.map((device) => device.macAddress).join(', ');
            presentFwLoading({
                spinner: null,
                animated: false,
                cssClass: 'fw-updating',
                message: `<div class="ion-text-center">
                            ${t`Updating firmware for ${macs}!`}
                            <br />
                            <br />
                            ${t`Please be patient, this may take around 5 minutes per device.`}
                        </div>`,
            });
            try {
                updating
                    ? await pollFirmwareStatusForDevices(devices)
                    : await updateDevicesFirmwareRequest(devices);
                await loadMeshStatus();
            } catch (e) {
                presentToast({
                    message: t`Oops, something went wrong while updating firmware! Please retry again and if the error persists, contact your supervisor.`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            await dismissFwLoading();
        },
        [dismissFwLoading, loadMeshStatus, presentFwLoading, presentToast]
    );

    const onButtonClick = useCallback(() => {
        if (allOnline && allFwUpToDate) {
            setTabsEnabled((prevState) => {
                return { ...prevState, test: true };
            });
            updateDBState();
            router.push(`/${jobId}/installation/test`, 'none');
        } else if (allFwUpToDate) {
            router.push(`/${jobId}/installation/mesh`, 'back');
        } else {
            // Update FW for all devices
            const updateAllDevicesWithOldFw = async () => {
                stopMeshStatusPolling();
                for (let i = 0; i < devicesWithOldFw.length; i += 1) {
                    await updateDeviceFw(devicesWithOldFw[i]);
                }
                startMeshStatusPolling();
            };
            updateAllDevicesWithOldFw();
        }
    }, [
        allFwUpToDate,
        allOnline,
        devicesWithOldFw,
        jobId,
        router,
        setTabsEnabled,
        startMeshStatusPolling,
        stopMeshStatusPolling,
        updateDBState,
        updateDeviceFw,
    ]);

    const getStatusElement = useCallback(
        (status?: IAssetStateData, fwStatus?: TFirmwareUptodateness) => {
            const badgeColor =
                status &&
                status.online &&
                [TFirmwareUptodateness.UPDATING].includes(fwStatus!)
                    ? 'info'
                    : status &&
                        status.online &&
                        [
                            TFirmwareUptodateness.UNKNOWN_VERSION,
                            TFirmwareUptodateness.TOOOLD,
                        ].includes(fwStatus!)
                      ? 'warning'
                      : status
                        ? status.online
                            ? 'success'
                            : 'danger'
                        : 'warning';
            const badgeText =
                status &&
                status.online &&
                fwStatus === TFirmwareUptodateness.UPDATING
                    ? t`Updating FW`
                    : status &&
                        status.online &&
                        [
                            TFirmwareUptodateness.UNKNOWN_VERSION,
                            TFirmwareUptodateness.TOOOLD,
                        ].includes(fwStatus!)
                      ? t`Need FW Update`
                      : status
                        ? status.online
                            ? t`Online`
                            : t`Offline`
                        : t`Unknown`;

            return (
                <IonBadge slot="end" color={badgeColor}>
                    {badgeText}
                </IonBadge>
            );
        },
        []
    );

    // useIonViewWillLeave(async () => {
    //     stopMeshStatusPolling();
    // });

    // TODO: work around because useIonViewWillLeave its not working
    // properly in nested routes
    // @see https://github.com/ionic-team/ionic-framework/issues/17992
    useEffect(() => {
        var init = async () => {
            const isInMeshStatus = !!matchPath<{ jobId?: string }>(
                location.pathname,
                {
                    path: '/:jobId/installation/mesh/status',
                    exact: false,
                    strict: false,
                }
            );

            if (!isInMeshStatus) {
                stopMeshStatusPolling();
            }
        };

        init();
    }, [location.pathname, stopMeshStatusPolling]);

    useEffect(() => {
        if (value.state === 'hasData') {
            // Delay loading mesh status so that the devices have some time
            // to send the announcement messages
            setTimeout(async () => {
                await loadMeshStatus();
                startMeshStatusPolling();
            }, 10000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.state]);

    useEffect(() => {
        const checkForDevicesBeingUpdated = async () => {
            if (devicesBeingUpdated && devicesBeingUpdated.length > 0) {
                stopMeshStatusPolling();
                await updateDeviceFw([...devicesBeingUpdated], true);
                startMeshStatusPolling();
                setDevicesBeingUpdated(undefined);
            }
        };
        checkForDevicesBeingUpdated();
    }, [
        devicesBeingUpdated,
        startMeshStatusPolling,
        stopMeshStatusPolling,
        updateDeviceFw,
    ]);

    return (
        <InterventionPage title={t`Mesh`} hideBackButton>
            <LoadableContent
                value={{
                    ...value,
                    state:
                        value.state === 'hasData' && !meshStatus
                            ? 'loading'
                            : value.state,
                }}
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                    disabled={refreshLoading}
                >
                    <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
                </IonRefresher>
                <IonList>
                    <IonListHeader
                        color={
                            allOnline && allFwUpToDate ? 'success' : 'warning'
                        }
                    >
                        <IonLabel>
                            <>
                                {allOnline && allFwUpToDate ? (
                                    <Trans>All devices in mesh</Trans>
                                ) : allFwUpToDate ? (
                                    <Trans>Mesh not concluded</Trans>
                                ) : (
                                    <Trans>FW update required</Trans>
                                )}
                                {allOnline && allFwUpToDate ? (
                                    <h6
                                        style={{ fontSize: '12px' }}
                                        dangerouslySetInnerHTML={{
                                            __html: t`All devices are online and up to date.`,
                                        }}
                                    />
                                ) : allFwUpToDate ? (
                                    <h6
                                        style={{ fontSize: '12px' }}
                                        dangerouslySetInnerHTML={{
                                            __html: t`- Check that the D-Box is correctly powered (wiring - electrical panel).</br>- Check that the customer's wifi is still active.`,
                                        }}
                                    />
                                ) : (
                                    <h6
                                        style={{ fontSize: '12px' }}
                                        dangerouslySetInnerHTML={{
                                            __html: t`We estimate the FW upgrade will take around 5 minutes per device.`,
                                        }}
                                    />
                                )}
                            </>
                        </IonLabel>
                    </IonListHeader>
                    <div className="ion-padding">
                        <LoadingButton
                            loading={refreshLoading}
                            onClick={onRefreshClick}
                        >
                            <Trans>Refresh Status</Trans>
                        </LoadingButton>
                        <IonButton
                            expand="block"
                            onClick={onButtonClick}
                            className="ion-margin-top"
                        >
                            {allOnline && allFwUpToDate ? (
                                <Trans>Proceed</Trans>
                            ) : allFwUpToDate ? (
                                <Trans>Recreate Mesh</Trans>
                            ) : (
                                <Trans>Start FW Update</Trans>
                            )}
                        </IonButton>
                    </div>
                    {rooms.map((room) => (
                        <IonItemGroup key={room.roomId}>
                            <IonItemDivider color="primary">
                                <IonLabel>{room.name}</IonLabel>
                            </IonItemDivider>
                            {room.devices.map((device: THomeAssetData) => {
                                const status =
                                    meshStatus &&
                                    meshStatus[device.homeAssetId];
                                const fwStatus =
                                    firmwareStatus &&
                                    firmwareStatus[
                                        device.macAddress.padStart(16, 'F')
                                    ];
                                return (
                                    <IonItem key={device.macAddress}>
                                        {getStatusElement(status, fwStatus)}
                                        <IonLabel>
                                            {device.macAddress}
                                        </IonLabel>
                                    </IonItem>
                                );
                            })}
                        </IonItemGroup>
                    ))}
                </IonList>
            </LoadableContent>
        </InterventionPage>
    );
};

export default MeshStatus;
